import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TriangleCalculator = () => {
  const [inputData, setInputData] = useState({
    calculationType: 'sides',
    a: '',
    b: '',
    c: '',
    A: '',
    B: '',
    C: '',
    base: '',
    height: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateTriangle = () => {
    const { calculationType, a, b, c, A, B, C, base, height } = inputData;

    // Clear previous results
    setResults(null);

    try {
      // Parse inputs
      const sideA = a ? parseFloat(a) : null;
      const sideB = b ? parseFloat(b) : null;
      const sideC = c ? parseFloat(c) : null;
      const angleA = A ? parseFloat(A) : null;
      const angleB = B ? parseFloat(B) : null;
      const angleC = C ? parseFloat(C) : null;
      const triangleBase = base ? parseFloat(base) : null;
      const triangleHeight = height ? parseFloat(height) : null;

      // Validate positive values
      if ([sideA, sideB, sideC, angleA, angleB, angleC, triangleBase, triangleHeight].some(
        val => val !== null && val <= 0
      )) {
        throw new Error('All values must be positive numbers');
      }

      let calculationResult;

      // SSS Calculation
      if (calculationType === 'sss' && sideA && sideB && sideC) {
        // Check triangle inequality
        if (sideA + sideB <= sideC || sideA + sideC <= sideB || sideB + sideC <= sideA) {
          throw new Error('Triangle inequality violated - the sum of any two sides must be greater than the third side');
        }

        // Calculate angles using Law of Cosines
        const angleARad = Math.acos((Math.pow(sideB, 2) + Math.pow(sideC, 2) - Math.pow(sideA, 2)) / (2 * sideB * sideC));
        const angleBRad = Math.acos((Math.pow(sideA, 2) + Math.pow(sideC, 2) - Math.pow(sideB, 2)) / (2 * sideA * sideC));
        const angleCRad = Math.PI - angleARad - angleBRad;

        // Calculate area using Heron's formula
        const s = (sideA + sideB + sideC) / 2;
        const area = Math.sqrt(s * (s - sideA) * (s - sideB) * (s - sideC));

        calculationResult = {
          sides: { a: sideA, b: sideB, c: sideC },
          angles: {
            A: (angleARad * 180 / Math.PI).toFixed(2),
            B: (angleBRad * 180 / Math.PI).toFixed(2),
            C: (angleCRad * 180 / Math.PI).toFixed(2)
          },
          perimeter: (sideA + sideB + sideC).toFixed(2),
          area: area.toFixed(2),
          calculationSteps: [
            `Step 1: Verify triangle inequality (satisfied)`,
            `Step 2: Calculate angles using Law of Cosines`,
            `Angle A: arccos((b² + c² - a²)/(2bc)) = ${angleARad.toFixed(4)} radians = ${(angleARad * 180 / Math.PI).toFixed(2)}°`,
            `Angle B: arccos((a² + c² - b²)/(2ac)) = ${angleBRad.toFixed(4)} radians = ${(angleBRad * 180 / Math.PI).toFixed(2)}°`,
            `Angle C: 180° - A - B = ${(angleCRad * 180 / Math.PI).toFixed(2)}°`,
            `Step 3: Calculate perimeter: a + b + c = ${sideA + sideB + sideC}`,
            `Step 4: Calculate area using Heron's formula: √[s(s-a)(s-b)(s-c)] = ${area.toFixed(2)}`
          ]
        };
      }

      // SAS Calculation
      else if (calculationType === 'sas' && sideA && sideB && angleC) {
        // Calculate third side using Law of Cosines
        const sideC = Math.sqrt(Math.pow(sideA, 2) + Math.pow(sideB, 2) - 2 * sideA * sideB * Math.cos(angleC * Math.PI / 180));

        // Calculate remaining angles
        const angleARad = Math.asin((sideA * Math.sin(angleC * Math.PI / 180)) / sideC);
        const angleBRad = Math.PI - angleARad - (angleC * Math.PI / 180);

        // Calculate area
        const area = (0.5 * sideA * sideB * Math.sin(angleC * Math.PI / 180)).toFixed(2);

        calculationResult = {
          sides: { a: sideA, b: sideB, c: sideC.toFixed(2) },
          angles: {
            A: (angleARad * 180 / Math.PI).toFixed(2),
            B: (angleBRad * 180 / Math.PI).toFixed(2),
            C: angleC.toFixed(2)
          },
          perimeter: (sideA + sideB + sideC).toFixed(2),
          area: area,
          calculationSteps: [
            `Step 1: Calculate side c using Law of Cosines: √(a² + b² - 2ab cos(C)) = ${sideC.toFixed(2)}`,
            `Step 2: Calculate angle A using Law of Sines: arcsin(a sin(C)/c) = ${(angleARad * 180 / Math.PI).toFixed(2)}°`,
            `Step 3: Calculate angle B: 180° - A - C = ${(angleBRad * 180 / Math.PI).toFixed(2)}°`,
            `Step 4: Calculate perimeter: a + b + c = ${(sideA + sideB + sideC).toFixed(2)}`,
            `Step 5: Calculate area: 0.5 * a * b * sin(C) = ${area}`
          ]
        };
      }

      // ASA Calculation
      else if (calculationType === 'asa' && angleA && angleB && sideC) {
        // Calculate remaining angle
        const angleC = 180 - angleA - angleB;

        // Calculate sides using Law of Sines
        const sideA = (sideC * Math.sin(angleA * Math.PI / 180)) / Math.sin(angleC * Math.PI / 180);
        const sideB = (sideC * Math.sin(angleB * Math.PI / 180)) / Math.sin(angleC * Math.PI / 180);

        // Calculate area
        const area = (0.5 * sideA * sideB * Math.sin(angleC * Math.PI / 180)).toFixed(2);

        calculationResult = {
          sides: { a: sideA.toFixed(2), b: sideB.toFixed(2), c: sideC },
          angles: {
            A: angleA.toFixed(2),
            B: angleB.toFixed(2),
            C: angleC.toFixed(2)
          },
          perimeter: (sideA + sideB + sideC).toFixed(2),
          area: area,
          calculationSteps: [
            `Step 1: Calculate angle C: 180° - A - B = ${angleC.toFixed(2)}°`,
            `Step 2: Calculate side a using Law of Sines: (c * sin(A))/sin(C) = ${sideA.toFixed(2)}`,
            `Step 3: Calculate side b using Law of Sines: (c * sin(B))/sin(C) = ${sideB.toFixed(2)}`,
            `Step 4: Calculate perimeter: a + b + c = ${(sideA + sideB + sideC).toFixed(2)}`,
            `Step 5: Calculate area: 0.5 * a * b * sin(C) = ${area}`
          ]
        };
      }

      // Area Calculation (base and height)
      else if (calculationType === 'area' && triangleBase && triangleHeight) {
        const area = (0.5 * triangleBase * triangleHeight).toFixed(2);

        calculationResult = {
          area: area,
          calculationSteps: [
            `Step 1: Calculate area using formula: 0.5 * base * height`,
            `Step 2: Substitute values: 0.5 * ${triangleBase} * ${triangleHeight} = ${area}`
          ]
        };
      }

      else {
        throw new Error('Please provide valid inputs for the selected calculation type');
      }

      // Create visualization data
      const chartData = {
        labels: ['Side a', 'Side b', 'Side c'],
        datasets: [{
          label: 'Triangle Sides',
          data: [
            calculationResult.sides?.a || 0,
            calculationResult.sides?.b || 0,
            calculationResult.sides?.c || 0
          ],
          backgroundColor: [
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(75, 192, 192, 0.5)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }]
      };

      setResults({
        ...calculationResult,
        chartData
      });

    } catch (err) {
      setError(err.message);
    }
  };

  const exampleData = {
    calculationType: 'sss',
    a: 5,
    b: 6,
    c: 7,
    perimeter: 18,
    area: 14.14
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-red-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Triangle Calculator | Calculate Triangle Properties</title>
          <meta name="description" content="Calculate various properties of triangles including sides, angles, perimeter, and area. This calculator supports multiple calculation methods like SSS, SAS, ASA, and area calculation using base and height." />
          <meta name="keywords" content="triangle calculator, calculate triangle sides, calculate triangle angles, triangle area, triangle perimeter, geometry calculator, math calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-red-600 mr-4">📐</div>
            <h1 className="text-3xl font-bold text-gray-800">Triangle Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate various properties of triangles including sides, angles, perimeter, and area. This calculator supports multiple calculation methods:</p>
            <ul className="list-disc pl-6 mt-4">
              <li>SSS (Side-Side-Side)</li>
              <li>SAS (Side-Angle-Side)</li>
              <li>ASA (Angle-Side-Angle)</li>
              <li>Area calculation using base and height</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Type:</label>
              <select
                name="calculationType"
                value={inputData.calculationType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="sides">SSS - Calculate angles from 3 sides</option>
                <option value="sas">SAS - Calculate from 2 sides and included angle</option>
                <option value="asa">ASA - Calculate from 2 angles and included side</option>
                <option value="area">Calculate area from base and height</option>
              </select>
            </div>

            {inputData.calculationType === 'sides' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side a:</label>
                  <input
                    type="number"
                    name="a"
                    value={inputData.a}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side a"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side b:</label>
                  <input
                    type="number"
                    name="b"
                    value={inputData.b}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side b"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side c:</label>
                  <input
                    type="number"
                    name="c"
                    value={inputData.c}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side c"
                  />
                </div>
              </>
            )}

            {inputData.calculationType === 'sas' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side a:</label>
                  <input
                    type="number"
                    name="a"
                    value={inputData.a}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side a"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side b:</label>
                  <input
                    type="number"
                    name="b"
                    value={inputData.b}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side b"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Angle C (°):</label>
                  <input
                    type="number"
                    name="C"
                    value={inputData.C}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter angle C in degrees"
                  />
                </div>
              </>
            )}

            {inputData.calculationType === 'asa' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Angle A (°):</label>
                  <input
                    type="number"
                    name="A"
                    value={inputData.A}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter angle A in degrees"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Angle B (°):</label>
                  <input
                    type="number"
                    name="B"
                    value={inputData.B}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter angle B in degrees"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Side c:</label>
                  <input
                    type="number"
                    name="c"
                    value={inputData.c}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter side c"
                  />
                </div>
              </>
            )}

            {inputData.calculationType === 'area' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Base:</label>
                  <input
                    type="number"
                    name="base"
                    value={inputData.base}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter base length"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height:</label>
                  <input
                    type="number"
                    name="height"
                    value={inputData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter height"
                  />
                </div>
              </>
            )}
          </div>

          <button
            onClick={calculateTriangle}
            className="w-full py-3 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Triangle Properties</h3>
                  {results.sides && (
                    <div className="mb-4">
                      <p className="text-lg">Sides:</p>
                      <ul className="list-disc pl-6 mt-2">
                        <li>a: {results.sides.a}</li>
                        <li>b: {results.sides.b}</li>
                        <li>c: {results.sides.c}</li>
                      </ul>
                    </div>
                  )}
                  {results.angles && (
                    <div className="mb-4">
                      <p className="text-lg">Angles:</p>
                      <ul className="list-disc pl-6 mt-2">
                        <li>A: {results.angles.A}°</li>
                        <li>B: {results.angles.B}°</li>
                        <li>C: {results.angles.C}°</li>
                      </ul>
                    </div>
                  )}
                  {results.perimeter && (
                    <p className="text-lg mt-2">Perimeter: {results.perimeter}</p>
                  )}
                  {results.area && (
                    <p className="text-lg mt-2">Area: {results.area}</p>
                  )}
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Triangle Visualization</h3>
                  <div className="h-48 bg-white rounded-lg flex items-center justify-center">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <polygon 
                        points="50,150 150,50 150,150" 
                        stroke="red" 
                        strokeWidth="2" 
                        fill="none"
                      />
                      <text x="50" y="150" fontSize="12" textAnchor="middle">A</text>
                      <text x="150" y="50" fontSize="12" textAnchor="middle">B</text>
                      <text x="150" y="150" fontSize="12" textAnchor="middle">C</text>
                    </svg>
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Length'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Sides'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Triangle Side Lengths'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-red-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a triangle with sides 5, 6, and 7:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Side a: {exampleData.a}</li>
                <li>Side b: {exampleData.b}</li>
                <li>Side c: {exampleData.c}</li>
                <li>Perimeter: {exampleData.perimeter}</li>
                <li>Area: {exampleData.area}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Verify triangle inequality (5 + 6 greater than 7, etc.)</li>
                <li>Calculate angles using Law of Cosines</li>
                <li>Calculate perimeter by summing all sides</li>
                <li>Calculate area using Heron's formula</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-red-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Triangle Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Calculation Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>SSS: Calculate angles from 3 sides</li>
                  <li>SAS: Calculate from 2 sides and included angle</li>
                  <li>ASA: Calculate from 2 angles and included side</li>
                  <li>Area: Calculate area from base and height</li>
                </ul>
              </li>
              <li><strong>Enter Required Values:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Provide the necessary inputs based on the selected calculation type</li>
                  <li>Ensure all values are positive numbers</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Missing sides or angles</li>
                  <li>Perimeter of the triangle</li>
                  <li>Area of the triangle</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-red-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>The sum of all angles in a triangle is always 180°</li>
                <li>The longest side is always opposite the largest angle</li>
                <li>Use SSS when you know all three sides</li>
                <li>Use SAS when you know two sides and the included angle</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Triangle Calculations <span className="text-red-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Triangles are fundamental geometric shapes with various properties and calculation methods:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Law of Cosines:</strong> Relates triangle sides to one angle</li>
              <li><strong>Law of Sines:</strong> Relates triangle sides to their opposite angles</li>
              <li><strong>Heron's Formula:</strong> Calculates area from three side lengths</li>
              <li><strong>Triangle Inequality:</strong> The sum of any two sides must be greater than the third side</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-red-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Verify Triangle Validity:</strong> Always check if the given sides can form a valid triangle</li>
              <li><strong>Use Consistent Units:</strong> Ensure all measurements are in the same unit</li>
              <li><strong>Double-check Inputs:</strong> Verify your inputs before calculating</li>
              <li><strong>Understand Limitations:</strong> Some triangle configurations may have multiple solutions</li>
            </ul>
            <p className="italic mt-4">
              Note: For precise engineering applications, consult with a professional for critical calculations.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TriangleCalculator;